import React, { useEffect, useState } from "react";
import { addDoc, collection, doc, getDoc, setDoc, updateDoc } from "firebase/firestore";
import { db, storage } from "../firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import EmployeesList from "./EmployeesList";

export default function EmployeeForm({ id: propId, onBack }) {
  const { id: paramId } = useParams();
  const id = propId || paramId;
  const navigate = useNavigate();

  const [form, setForm] = useState({
    accid: "",
    empId: "",
    uid: "",
    name: "",
    mobile: "",
    email: "",
    designation: "",
    dob: "",
    doj: "",
    aadhar: "",
    dl: "",
    pan: "",
    addr: "",
    monthlySalary: "",
    status: "Active",
    category: "Regular",
    aadharUrl: "",
    dlUrl: "",
    panUrl: "",
    biodataUrl: "",
    salesforceId: "",
  });
  const [files, setFiles] = useState({
    aadhar: null,
    dl: null,
    pan: null,
    biodata: null,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (id) loadEmployee();
  }, [id]);

  const loadEmployee = async () => {
    const ref = doc(db, "EMPLOYEES", id);
    const snap = await getDoc(ref);
    if (snap.exists()) setForm(snap.data());
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    setFiles({ ...files, [e.target.name]: e.target.files[0] });
  };

  const uploadFile = async (file, folder) => {
    if (!file) return null;
    try {
      console.log(`🚀 Starting upload: ${folder}/${file.name}`);
      const fileRef = ref(storage, `employees/${folder}/${Date.now()}_${file.name}`);
      const uploadTask = await uploadBytes(fileRef, file);
      const url = await getDownloadURL(fileRef);
      console.log(`✅ Upload success: ${url}`);
      return url;
    } catch (error) {
      console.error(`❌ Upload failed for ${folder}:`, error);
      throw new Error(`Failed to upload ${folder}: ${error.message}`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      console.log("📝 Form submission started...");

      const urls = { ...form };
      if (files.aadhar) {
        toast.info("Uploading Aadhar...");
        urls.aadharUrl = await uploadFile(files.aadhar, "aadhar");
      }
      if (files.dl) {
        toast.info("Uploading DL...");
        urls.dlUrl = await uploadFile(files.dl, "dl");
      }
      if (files.pan) {
        toast.info("Uploading PAN Card...");
        urls.panUrl = await uploadFile(files.pan, "pan");
      }
      if (files.biodata) {
        toast.info("Uploading Biodata...");
        urls.biodataUrl = await uploadFile(files.biodata, "biodata");
      }

      console.log("💾 Saving to Firestore...");
      let finalUid = urls.uid;
      if (urls.category === "Contract" && !finalUid) {
        finalUid = `CONT_${Date.now()}`;
        urls.uid = finalUid;
      }

      if (!finalUid) throw new Error("UID is required for Regular employees.");

      const empRef = doc(db, "EMPLOYEES", finalUid);
      await setDoc(empRef, urls, { merge: true });

      // Mirror to 'users' collection ONLY for Regular employees
      if (urls.category !== "Contract") {
        const userRef = doc(db, "users", finalUid);
        await setDoc(userRef, {
          uid: finalUid,
          name: urls.name,
          email: urls.email || "",
          mobile: urls.mobile,
          role: "employee",
          empId: finalUid, // Linking by UID
        }, { merge: true });
      }

      toast.success("Employee and user record saved successfully!");
      if (onBack) {
        onBack();
      } else {
        navigate("/AdminDashboard");
      }
    } catch (err) {
      console.error("❌ Submission error:", err);
      alert(`Error: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate("/AdminDashboard");
    }
  };

  return (
    <div className="container-fluid mt-4 px-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3 className="fw-bold text-dark mb-0">{id ? "Edit Employee" : "Add Employee"}</h3>
        <button className="btn btn-secondary btn-sm shadow-sm" onClick={handleBack}>
          <i className="bi bi-chevron-left me-1"></i> Back
        </button>
      </div>

      <div className="card p-3">
        <form onSubmit={handleSubmit}>
          {/* Section 1: Basic Information */}
          <div className="mb-4 pb-2 border-bottom">
            <h6 className="fw-bold"><i className="bi bi-person me-2"></i>Basic Information</h6>
          </div>
          <div className="row g-3 mb-4">
            <div className="col-md-3">
              <label className="form-label">Category</label>
              <select className="form-select" name="category" value={form.category} onChange={handleChange}>
                <option value="Regular">Regular</option>
                <option value="Contract">Contract</option>
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Employee ID</label>
              <input type="text" name="accid" value={form.accid} onChange={handleChange} className="form-control" placeholder="e.g. ACC001" />
            </div>
            <div className="col-md-3">
              <label className="form-label">Salesforce ID</label>
              <input type="text" name="salesforceId" value={form.salesforceId} onChange={handleChange} className="form-control" placeholder="e.g. SF123" />
            </div>
            {form.category !== "Contract" && (
              <div className="col-md-6">
                <label className="form-label">UID (from Firebase Auth)</label>
                <input
                  type="text"
                  name="uid"
                  value={form.uid}
                  onChange={handleChange}
                  className="form-control"
                  placeholder="Paste UID here"
                  required
                  disabled={!!id}
                />
              </div>
            )}
            <div className="col-md-4">
              <label className="form-label">Full Name</label>
              <input type="text" name="name" value={form.name} onChange={handleChange} className="form-control" placeholder="Enter name" />
            </div>
            <div className="col-md-4">
              <label className="form-label">Mobile Number</label>
              <input type="text" name="mobile" value={form.mobile} onChange={handleChange} className="form-control" placeholder="e.g. 98831*****" />
            </div>
            <div className="col-md-4">
              <label className="form-label">Email Address</label>
              <input type="email" name="email" value={form.email} onChange={handleChange} className="form-control" placeholder="example@email.com" />
            </div>
          </div>

          {/* Section 2: Employment Details */}
          <div className="mb-4 pb-2 border-bottom">
            <h6 className="fw-bold"><i className="bi bi-briefcase me-2"></i>Employment Details</h6>
          </div>
          <div className="row g-3 mb-4">
            <div className="col-md-4">
              <label className="form-label">Designation</label>
              <input type="text" name="designation" value={form.designation} onChange={handleChange} className="form-control" placeholder="e.g. Technician" />
            </div>
            <div className="col-md-4">
              <label className="form-label">Monthly Salary (₹)</label>
              <input type="number" name="monthlySalary" value={form.monthlySalary} onChange={handleChange} className="form-control" placeholder="0.00" />
            </div>
            <div className="col-md-4">
              <label className="form-label">Employment Status</label>
              <select className="form-select" name="status" value={form.status} onChange={handleChange}>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Date of Birth</label>
              <input type="date" name="dob" value={form.dob} onChange={handleChange} className="form-control" />
            </div>
            <div className="col-md-3">
              <label className="form-label">Date of Joining</label>
              <input type="date" name="doj" value={form.doj} onChange={handleChange} className="form-control" />
            </div>
            <div className="col-12">
              <label className="form-label">Address</label>
              <textarea name="addr" value={form.addr} onChange={handleChange} className="form-control" rows="2" placeholder="Residential Address"></textarea>
            </div>
          </div>

          {/* Section 3: Identity & Documents */}
          <div className="mb-4 pb-2 border-bottom">
            <h6 className="fw-bold"><i className="bi bi-file-earmark-medical me-2"></i>Identity & Documents</h6>
          </div>
          <div className="row g-3 mb-4">
            <div className="col-md-6">
              <div className="p-3 border rounded">
                <label className="form-label fw-bold">Aadhar Card</label>
                <div className="input-group mb-2">
                  <input type="text" name="aadhar" value={form.aadhar} onChange={handleChange} className="form-control" placeholder="Aadhar number" />
                  {form.aadharUrl && <a href={form.aadharUrl} target="_blank" rel="noreferrer" className="btn btn-outline-info"><i className="bi bi-eye"></i></a>}
                </div>
                <input type="file" name="aadhar" onChange={handleFileChange} className="form-control form-control-sm" accept="image/*,.pdf" />
              </div>
            </div>

            <div className="col-md-6">
              <div className="p-3 border rounded">
                <label className="form-label fw-bold">Driving License (DL)</label>
                <div className="input-group mb-2">
                  <input type="text" name="dl" value={form.dl} onChange={handleChange} className="form-control" placeholder="DL number" />
                  {form.dlUrl && <a href={form.dlUrl} target="_blank" rel="noreferrer" className="btn btn-outline-info"><i className="bi bi-eye"></i></a>}
                </div>
                <input type="file" name="dl" onChange={handleFileChange} className="form-control form-control-sm" accept="image/*,.pdf" />
              </div>
            </div>

            <div className="col-md-6">
              <div className="p-3 border rounded">
                <label className="form-label fw-bold">PAN Card</label>
                <div className="input-group mb-2">
                  <input type="text" name="pan" value={form.pan} onChange={handleChange} className="form-control" placeholder="PAN number" />
                  {form.panUrl && <a href={form.panUrl} target="_blank" rel="noreferrer" className="btn btn-outline-info"><i className="bi bi-eye"></i></a>}
                </div>
                <input type="file" name="pan" onChange={handleFileChange} className="form-control form-control-sm" accept="image/*,.pdf" />
              </div>
            </div>

            <div className="col-md-6">
              <div className="p-3 border rounded">
                <label className="form-label fw-bold">Biodata / Others</label>
                <div className="d-flex align-items-center">
                  <input type="file" name="biodata" onChange={handleFileChange} className="form-control form-control-sm" accept="image/*,.pdf" />
                  {form.biodataUrl && <a href={form.biodataUrl} target="_blank" rel="noreferrer" className="btn btn-outline-info ms-2 btn-sm"><i className="bi bi-eye"></i></a>}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 pt-3 border-top">
            <button type="submit" className="btn btn-success px-5" disabled={saving}>
              {saving ? <span className="spinner-border spinner-border-sm me-2" /> : (id ? "Update Employee" : "Save Employee")}
            </button>
            <button type="button" className="btn btn-secondary px-4 ms-2" onClick={handleBack}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}
